import React, { useState, useEffect } from "react";
import AnimationRevealPage from "helpers/AnimationRevealPage.js";
import { Container, ContentWithPaddingXl } from "components/misc/Layouts";
import tw from "twin.macro";
import styled from "styled-components";
import Slider from "react-slick";
import FooterCard from "components/footers/PageFooter";
import { SectionHeading } from "components/misc/Headings";
import { PrimaryButton } from "components/misc/Buttons";
import PageHeader from "components/headers/PageHeader";
import { PrimaryButton as PrimaryButtonBase } from "components/misc/Buttons.js";
import { FaStar } from "react-icons/fa";
import { Subheading as SubheadingBase } from "components/misc/Headings.js";
import { ReactComponent as StarIconBase } from "images/star-icon.svg";
import loveIllustrationImageSrc from "images/love.svg";
import { ReactComponent as ArrowLeftIcon } from "images/arrow-left-3-icon.svg";
import { ReactComponent as ArrowRightIcon } from "images/arrow-right-3-icon.svg";

import { getDownloadURL, ref as storageRef, uploadBytes, deleteObject } from "firebase/storage";

import BlogIllustrate from "images/blog_ills.jpg";
import { FaCheckCircle, FaTimesCircle, FaInfoCircle } from 'react-icons/fa';
import { app, analytics, storage } from "../firebaseConfig";

const API_URL = process.env.REACT_APP_IS_PROD == "true" ? process.env.REACT_APP_API_URL_PROD : process.env.REACT_APP_API_URL_DEV


const HeadingRow = tw.div`flex justify-between items-center mb-8`;
const Heading = tw(SectionHeading)`lg:text-3xl text-lg mt-4 lg:ml-24 text-center`;
const Label = tw.label`block font-semibold text-gray-700 mt-4`;
const TextField = styled.input`
  ${tw`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0049AC]`}
`;
const TextArea = styled.textarea`
  ${tw`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0049AC]`}
`;
const FileInput = styled.input`
  ${tw`w-full py-2`}
`;
const Dropdown = styled.select`
  ${tw`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0049AC]`}
`;
const StarRating = tw.div`flex items-center justify-center space-x-4 mb-6`;
const ReviewsList = tw.div`mt-12`;
const ReviewCard = tw.div`bg-white p-6 rounded-lg shadow-md mb-6`;

const Row = tw.div`flex flex-col md:flex-row justify-between items-center`;
const Column = tw.div`w-full max-w-md mx-auto md:max-w-none md:mx-0`;
const ImageColumn = tw(Column)`md:w-5/12 xl:w-6/12 flex-shrink-0 relative`;


const Form = tw.form`bg-gray-100 p-8 rounded-lg shadow-md w-full lg:w-3/4 mx-auto`; // Center the form and set it to full-width
const TextColumn = styled(Column)(props => [
  tw`lg:w-3/4 w-full mt-16 md:mt-0 mx-auto`, // Full width and center on small screens
  props.textOnLeft ? tw`md:pr-12 lg:pr-16 md:order-last` : tw`md:pl-12 lg:pl-16 md:order-last`
]);
const Input = tw.input`border rounded w-full py-2 px-3 text-gray-700 focus:outline-none focus:shadow-outline`;
const Button = tw(PrimaryButton)`absolute right-0 top-0 px-4 py-2 text-sm`;
const ThumbnailButton = styled(Button)`
  ${tw`bg-blue-500 text-white`}
`;
const AddThumbnailContainer = tw.div`relative flex items-center`;


const Image = styled.img(props => [
  props.imageRounded && tw`rounded`,
  props.imageBorder && tw`border`,
  props.imageShadow && tw`shadow`
]);

const Subheading = tw(SubheadingBase)`text-center md:text-left text-3xl`;
const Description = tw.p`mt-6 text-center md:text-left text-sm md:text-base lg:text-lg font-medium leading-relaxed text-secondary-100`;

const TestimonialSlider = styled(Slider)`
  ${tw`w-full mt-10 text-center md:text-left`}
  .slick-track {
    ${tw`flex`}
  }
  .slick-slide {
    ${tw`h-auto flex justify-center mb-1`}
  }
`;

const Testimonial = tw.div`outline-none h-full flex! flex-col`;
const StarsContainer = styled.div``;
const StarIcon = tw(StarIconBase)`inline-block w-5 h-5 text-orange-400 fill-current mr-1 last:mr-0`;
const TestimonialHeading = tw.div`mt-4 text-xl font-bold`;
const Quote = tw.blockquote`mt-4 mb-8 sm:mb-10 leading-relaxed font-medium text-gray-700`;

const CustomerInfoAndControlsContainer = tw.div`mt-auto flex justify-between items-center flex-col sm:flex-row`;

const CustomerInfo = tw.div`flex flex-col sm:flex-row items-center justify-center lg:justify-start`;
const CustomerProfilePicture = tw.img`rounded-full w-16 h-16 sm:w-20 sm:h-20`;
const CustomerTextInfo = tw.div`text-center md:text-left sm:ml-6 mt-2 sm:mt-0`;
const CustomerName = tw.h5`font-bold text-xl`;
const CustomerTitle = tw.p`font-medium text-secondary-100`;

const Controls = styled.div`
  ${tw`flex mt-8 sm:mt-0`}
  .divider {
    ${tw`my-3 border-r`}
  }
`;
const ControlButton = styled.button`
  ${tw`mx-3 p-4 rounded-full transition duration-300 bg-gray-200 hover:bg-gray-300 text-[#0049AC] hover:text-[#0049AC] focus:outline-none focus:shadow-outline`}
  svg {
    ${tw`w-4 h-4 stroke-3`}
  }
`;

const SubmitButton = tw(PrimaryButtonBase)`inline-block mt-8 flex justify-center items-center`;
const ProgressCircle = tw.div`w-10 h-10 border-4 border-t-4 border-[#0049AC] rounded-full animate-spin`;

const PrimaryAction = tw.button`rounded-full px-6 py-3 mt-5 text-sm sm:text-base sm:mt-16 sm:px-8 sm:py-4 bg-gray-100 font-bold shadow transition duration-300 bg-[#0049AC] text-gray-100 hocus:text-gray-200 focus:outline-none focus:shadow-outline`;

const DialogBox = styled.div`
  ${tw`fixed top-0 left-0 w-full h-full flex justify-center items-center bg-gray-500 bg-opacity-50 z-50`}
  display: ${({ show }) => (show ? "flex" : "none")};
  animation: fadeIn 0.3s ease-in-out;
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;
const DialogContent = tw.div`bg-white p-6 rounded-lg shadow-xl text-center w-[90%] max-w-md`;
const DialogHeading = tw.h2`text-xl font-bold text-gray-900`;
const DialogMessage = tw.p`mt-4 text-sm text-gray-700`;
const OkButton = tw.button`mt-4 px-4 py-2 bg-[#0049AC] text-white rounded-full`;

const categories = ["Destination", "Tour Packages", "Hotels", "Hotel Resort", "Overall"];
const categoryDependentItems = {
  Destination: ["Beaches", "Mountains", "Cities"],
  "Tour Packages": ["Adventure", "Luxury", "Budget"],
  Hotels: ["5-Star", "4-Star", "3-Star"],
  "Hotel Resort": ["Luxury Resort", "Beachside Resort"],
};

const fallbackReviews = [
  {
    stars: 5,
    profileImageSrc: "https://randomuser.me/api/portraits/men/32.jpg",
    heading: "Amazing Experience",
    quote: "This was the best experience ever! The service was incredible and the views were breathtaking.",
    customerName: "John Doe",
    customerTitle: "Travel Blogger"
  },
  {
    stars: 4,
    profileImageSrc: "https://randomuser.me/api/portraits/men/45.jpg",
    heading: "Great Getaway",
    quote: "Had a wonderful time! The staff was friendly and the activities were well organized.",
    customerName: "Jane Smith",
    customerTitle: "Vacationer"
  },
  {
    stars: 5,
    profileImageSrc: "https://randomuser.me/api/portraits/women/32.jpg",
    heading: "An Unforgettable Journey",
    quote: "This trip was absolutely amazing! The team was fantastic, and the destination exceeded my expectations. Highly recommend it!",
    customerName: "Emily Brown",
    customerTitle: "Travel Enthusiast"
  },
  {
    stars: 4,
    profileImageSrc: "https://randomuser.me/api/portraits/men/58.jpg",
    heading: "Incredible Service",
    quote: "The staff was professional and friendly. Everything was well-organized, and I had a relaxing experience. Can't wait for the next one!",
    customerName: "Chris Williams",
    customerTitle: "Business Traveler"
  },
  {
    stars: 3,
    profileImageSrc: "https://randomuser.me/api/portraits/women/11.jpg",
    heading: "Good but Could be Better",
    quote: "It was a good vacation, but there were a few things that could be improved. Overall, a decent experience with some room for improvement.",
    customerName: "Olivia Green",
    customerTitle: "Solo Traveler"
  },
  {
    stars: 5,
    profileImageSrc: "https://randomuser.me/api/portraits/men/71.jpg",
    heading: "Absolutely Perfect!",
    quote: "The entire trip was fantastic. From the moment we arrived, everything was taken care of. This is the ultimate way to travel.",
    customerName: "Michael Davis",
    customerTitle: "Adventure Seeker"
  },
  {
    stars: 4,
    profileImageSrc: "https://randomuser.me/api/portraits/women/27.jpg",
    heading: "Great Experience with Minor Issues",
    quote: "The experience was great, although the weather didn't cooperate at times. Still, a trip I will always remember. Would recommend!",
    customerName: "Sophia Martinez",
    customerTitle: "Family Traveler"
  }
];

export default ({
  imageSrc = loveIllustrationImageSrc,
  imageRounded = true,
  imageBorder = false,
  imageShadow = false,
  subheading = "We Value Your Experience",
  heading = "",
  description = "Your voice matters to us! Share your experience and help us elevate our service to new heights. We’re grateful for your time and feedback—together, we can keep growing and exceeding expectations.",
  textOnLeft = true
}) => {

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [sliderRef, setSliderRef] = useState(null);
  const [profile, setProfile] = useState(null);
  const [reviewForm, setReviewForm] = useState({ name: "", designation: "", profile_pic: "", rating: 5, review: "", destination_id: null, resort_id: null, tour_id: null });
  const [isSuccess, setIsSuccess] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [message, setMessage] = useState('');
  const [fieldError, setFieldError] = useState(false);

  useEffect(() => {
    document.body.style.overflow = 'auto'
  });

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const response = await fetch(`${API_URL}/web/reviews/review-list`, {
          headers: {
            'X-API-Key': process.env.REACT_APP_ACCESS_API_KEY // Add API key to headers
          }
        });
        const result = await response.json();
        const data = result.data;
        if (result.header.resCode === 200 && data.length > 0) {
          setReviews(data.map(review => ({
            stars: review.rating,
            profileImageSrc: review.profile_pic,
            heading: review.review_text.substring(0, 30), // first 30 characters as heading
            quote: review.review_text,
            customerName: review.user_name,
            customerTitle: review.designation
          })));
        }
      } catch (error) {
        console.error("Error fetching reviews:", error);
      }
    };

    fetchReviews();
  }, []);


  const handleReviewDataChange = (e) => {
    const { name, value } = e.target;
    setReviewForm({ ...reviewForm, [name]: value });
  };

  const handleRating = (rating) => {
    setReviewForm({ ...reviewForm, rating });
  };

  const validateReviewForm = () => {

    if (reviewForm.rating == 0) {
      setFieldError(true);
      setIsSuccess(false);
      setShowDialog(true);
      document.body.style.overflow = 'hidden';
      setMessage('Give rating by clicking on stars.');
      return false;
    }
    if (reviewForm.review == "") {
      setFieldError(true);
      setIsSuccess(false);
      setShowDialog(true);
      document.body.style.overflow = 'hidden';
      setMessage('Write review thoughts.');
      return false;
    }
    // if (reviewForm.destination_id == null && reviewForm.resort_id && reviewForm.tour_id) {
    //   setFieldError(true);
    //   setIsSuccess(false);
    //   document.body.style.overflow = 'hidden';
    //   setShowDialog(true);
    //   setMessage('Please Select Review Item');
    //   return false;
    // }
    if (reviewForm.name == "") {
      setFieldError(true);
      setIsSuccess(false);
      document.body.style.overflow = 'hidden';
      setShowDialog(true);
      setMessage('Write your name.');
      return false;
    }
    if (reviewForm.designation == "") {
      setFieldError(true);
      setIsSuccess(false);
      document.body.style.overflow = 'hidden';
      setShowDialog(true);
      setMessage('Write your designation.');
      return false;
    }

    if (reviewForm.profile_pic == "" && profile == null) {
      setFieldError(true);
      setIsSuccess(false);
      setShowDialog(true);
      document.body.style.overflow = 'hidden';
      setMessage('Please add your profile picture.');
      return false;
    }
    return true;
  };



  const handleCloseDialog = () => {
    document.body.style.overflow = 'auto';
    setShowDialog(false);
    setMessage('')
    setFieldError(false)
  };

  const getCurrentTime = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0"); // Months are zero-based
    const day = String(now.getDate()).padStart(2, "0");
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    const seconds = String(now.getSeconds()).padStart(2, "0");

    return `${year}_${month}_${day}_${hours}_${minutes}_${seconds}`;
  };

  const handleProfileUpload = (e) => {
    setProfile(e.target.files[0])
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    let profileRef = null

    if (validateReviewForm()) {
      setIsSubmitting(true);

      let profilePicUrl = reviewForm.profile_pic;

      if (profile != null) {
        profileRef = storageRef(storage, `user_profile/${getCurrentTime()}`);

        const snapshot = await uploadBytes(profileRef, profile)
        const url = await getDownloadURL(snapshot.ref)
        profilePicUrl = url

      } else if (reviewForm.profile_pic == "") {
        setFieldError(true);
        setIsSuccess(false);
        setShowDialog(true);
        document.body.style.overflow = 'hidden';
        setMessage('Please add proper link or upload profile picture.');
        return false;
      }

      const updatedReviewForm = { ...reviewForm, profile_pic: profilePicUrl };
      setReviewForm(updatedReviewForm);

      try {
        const response = await fetch(`${API_URL}/web/reviews/submit-review`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            'X-API-Key': process.env.REACT_APP_ACCESS_API_KEY // Add API key to headers
          },
          body: JSON.stringify(updatedReviewForm),
        });

        if (response.ok) {
          setIsSuccess(true)
          setProfile(null)
          setReviewForm({ name: "", designation: "", profile_pic: "", rating: 5, review: "", destination_id: null, resort_id: null, tour_id: null })
        } else {
          setIsSuccess(false)
          if (profileRef) {
            try {
              await deleteObject(profileRef);
            } catch (deleteError) {
              console.error("Error deleting profile picture:", deleteError.message);
            }
          }

        }
      } catch (error) {

        if (profileRef) {
          try {
            await deleteObject(profileRef);
          } catch (deleteError) {
            console.error("Error deleting profile picture:", deleteError.message);
          }
        }
        setIsSuccess(false);
        setMessage('Some error occured..')
        setFieldError(false)
      } finally {
        setIsSubmitting(false);
        setShowDialog(true);
      }

    }
  };

  return (
    <AnimationRevealPage>
      <PageHeader pageName="Reviews" titleName="Feedback" />
      <Container>
        <HeadingRow>
          <Heading>Your Voice Matters – Share Your Experience and Inspire Others!</Heading>
        </HeadingRow>
        <Form>
          <StarRating>
            {[1, 2, 3, 4, 5].map((star) => (
              <FaStar
                key={star}
                size={48}
                onClick={() => handleRating(star)}
                color={reviewForm.rating >= star ? "#ffc107" : "#e4e5e9"}
                style={{ cursor: "pointer" }}
              />
            ))}
          </StarRating>

          <Label>Thoughts to Inspire Others</Label>
          <TextArea
            name="review"
            value={reviewForm.review}
            onChange={handleReviewDataChange}
            placeholder="Write your review here..."
            rows="4"
            required
          />

          {/* <Label>Category</Label>
          <Dropdown name="category" value={reviewForm.category} onChange={handleReviewDataChange}>
            {categories.map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </Dropdown> */}

          {/* {reviewForm.category !== "Overall" && (
            <>
              <Label>Category Dependent Items</Label>
              <Dropdown
                name="dependentItem"
                value={reviewForm.dependentItem}
                onChange={handleReviewDataChange}
                required
              >
                <option value="" disabled>
                  Select an option
                </option>
                {categoryDependentItems[reviewForm.category].map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </Dropdown>
            </>
          )} */}

          <Label>Name</Label>
          <TextField
            type="text"
            name="name"
            value={reviewForm.name}
            onChange={handleReviewDataChange}
            placeholder="Enter your name"
            required
          />

          <Label>Designation</Label>
          <TextField
            type="text"
            name="designation"
            value={reviewForm.designation}
            onChange={handleReviewDataChange}
            placeholder="Enter your designation"
          />
          {/* 
          <Label>Email</Label>
          <TextField
            type="email"
            name="email"
            value={reviewForm.email}
            onChange={handleReviewDataChange}
            placeholder="Enter your email"
            required
          /> */}

          {/* <Label>Phone</Label>
          <TextField
            type="text"
            name="phone"
            value={reviewForm.phone}
            onChange={handleReviewDataChange}
            placeholder="Enter your phone number"
          /> */}

          <div>
            <Label htmlFor="profile">Profile Picture</Label>
            <AddThumbnailContainer>
              <Input
                id="profile"
                type="text"
                name="profile_pic"
                placeholder="Paste profile link or upload picture"
                value={profile == null ? reviewForm.profile_pic : profile.name}
                onChange={handleReviewDataChange}
              />
              <ThumbnailButton as="label" htmlFor="profile-upload">
                Upload
                <input
                  id="profile-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleProfileUpload}
                  style={{ display: "none" }}
                />
              </ThumbnailButton>
            </AddThumbnailContainer>
          </div>

          <SubmitButton type="submit" disabled={isSubmitting} onClick={handleSubmit}>
            {isSubmitting ? <ProgressCircle /> : "Submit Review"}
          </SubmitButton>
        </Form>
        <br /><br />
        <TextColumn textOnLeft={textOnLeft}>
          <Subheading>{subheading}</Subheading>
          <Heading>{heading}</Heading>
          <Description>{description}</Description>
          <TestimonialSlider arrows={false} ref={setSliderRef}>
            {reviews.map((review, index) => (
              <Testimonial key={index}>
                <StarsContainer>
                  {Array.from({ length: review.stars }).map((_, indexIcon) => (
                    <StarIcon key={indexIcon} />
                  ))}
                </StarsContainer>
                <TestimonialHeading>{review.heading}</TestimonialHeading>
                <Quote>{review.quote}</Quote>
                <CustomerInfoAndControlsContainer>
                  <CustomerInfo>
                    <CustomerProfilePicture src={review.profileImageSrc} alt={review.customerName} />
                    <CustomerTextInfo>
                      <CustomerName>{review.customerName}</CustomerName>
                      <CustomerTitle>{review.customerTitle}</CustomerTitle>
                    </CustomerTextInfo>
                  </CustomerInfo>
                  <Controls>
                    <ControlButton onClick={sliderRef?.slickPrev}>
                      <ArrowLeftIcon />
                    </ControlButton>
                    <div className="divider" />
                    <ControlButton onClick={sliderRef?.slickNext}>
                      <ArrowRightIcon />
                    </ControlButton>
                  </Controls>
                </CustomerInfoAndControlsContainer>
              </Testimonial>
            ))}
          </TestimonialSlider>
        </TextColumn>
        <br /><br />
        <DialogBox show={showDialog}>
          <DialogContent>
            <DialogHeading className="dialog-heading">
              {isSuccess ? (
                <FaCheckCircle className="success-icon" />
              ) : fieldError ? (
                <FaInfoCircle className="info-icon" />
              ) : (
                <FaTimesCircle className="error-icon" />
              )}
            </DialogHeading>
            <DialogMessage>
              {isSuccess ? "Thank you for review. " : fieldError ? message : "We're facing issue submitting your review. Please try again."}
            </DialogMessage>
            <OkButton onClick={handleCloseDialog}>OK</OkButton>
          </DialogContent>
        </DialogBox>
      </Container>
      <FooterCard />
    </AnimationRevealPage>
  );
};
